<script setup lang="ts">
import { History } from 'lucide-vue-next';

const prefix = 'components.legal.accessibility';
</script>

<template>
  <div>
    <div class="w-full bg-secondary px-4 pb-5 pt-10 md:px-6 lg:px-8">
      <UiCustomContainer>
        <NuxtLinkLocale
          to="index"
          class="mb-8 inline-flex items-center gap-2 text-text-primary-600 hover:text-text-primary-800"
        >
          <SvgoCommonArrowLeft
            filled
            :font-controlled="false"
            class="mt-0.5 h-5 w-5"
          />
          <span class="font-semibold">{{ $t(`${prefix}.backToHome`) }}</span>
        </NuxtLinkLocale>

        <div class="mb-10 flex flex-col items-center gap-4">
          <h1 class="text-pretty text-center text-3xl font-bold">
            {{ $t('pages.accessibility.seo.title') }}
          </h1>

          <UiAlert
            variant="warning"
            size="s"
            class="w-fit items-center text-orange-800"
          >
            <span class="text-base font-semibold">{{ $t(`${prefix}.complianceStatus`) }}</span>
          </UiAlert>
        </div>
      </UiCustomContainer>
    </div>

    <div class="flex w-full justify-center px-4 py-10 md:px-6 lg:px-8">
      <div class="flex max-w-container flex-col gap-6 text-text-secondary-600">
        <div class="flex items-center gap-2 text-sm text-gray-600">
          <History
            class="size-5 shrink-0"
            aria-hidden="true"
          />
          <span>{{ $t(`${prefix}.updatedAtLabel`) }}</span>
          <time datetime="2026-08-31">{{ $t(`${prefix}.updatedAtDate`) }}</time>
        </div>

        <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.intro`) }}</p>

        <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.summary`) }}</p>

        <nav
          aria-labelledby="accessibility-summary"
          class="rounded-lg bg-gray-50 px-6 py-5"
        >
          <h2
            id="accessibility-summary"
            class="pb-3 text-lg font-medium text-gray-900 md:text-xl"
          >
            {{ $t(`${prefix}.summaryNav.title`) }}
          </h2>

          <ol class="flex list-inside list-decimal flex-col gap-2">
            <li class="text-base font-normal md:text-lg">
              <NuxtLink
                to="#accessibility-declaration"
                class="text-gray-900 underline underline-offset-2 hover:text-brand-primary-700"
              >
                {{ $t(`${prefix}.declaration.title`) }}
              </NuxtLink>
            </li>
            <li class="text-base font-normal md:text-lg">
              <NuxtLink
                to="#accessibility-scheme"
                class="text-gray-900 underline underline-offset-2 hover:text-brand-primary-700"
              >
                {{ $t(`${prefix}.scheme.title`) }}
              </NuxtLink>
            </li>
            <li class="text-base font-normal md:text-lg">
              <NuxtLink
                to="#accessibility-action-plan"
                class="text-gray-900 underline underline-offset-2 hover:text-brand-primary-700"
              >
                {{ $t(`${prefix}.actionPlan.title`) }}
              </NuxtLink>
            </li>
            <li class="text-base font-normal md:text-lg">
              <NuxtLink
                to="#accessibility-report"
                class="text-gray-900 underline underline-offset-2 hover:text-brand-primary-700"
              >
                {{ $t(`${prefix}.report.title`) }}
              </NuxtLink>
            </li>
            <li class="text-base font-normal md:text-lg">
              <NuxtLink
                to="#accessibility-remedies"
                class="text-gray-900 underline underline-offset-2 hover:text-brand-primary-700"
              >
                {{ $t(`${prefix}.remedies.title`) }}
              </NuxtLink>
            </li>
          </ol>
        </nav>

        <UiCustomDocumentSection
          anchor="accessibility-declaration"
          :index="1"
          :title="$t(`${prefix}.declaration.title`)"
        >
          <h3 class="text-lg font-medium md:text-xl">{{ $t(`${prefix}.declaration.complianceStatusTitle`) }}</h3>
          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.declaration.p1`) }}</p>
          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.declaration.p2`) }}</p>
          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.declaration.p3`) }}</p>

          <h3 class="pt-2 text-lg font-medium md:text-xl">{{ $t(`${prefix}.declaration.timelineTitle`) }}</h3>
          <ol class="flex flex-col gap-4 border-l-2 border-gray-200 pl-6">
            <li>
              <div class="flex flex-col gap-1">
                <time
                  datetime="2026-11-02"
                  class="text-sm font-semibold uppercase tracking-wider text-text-accent-700"
                >
                  {{ $t(`${prefix}.declaration.timeline.step1.date`) }}
                </time>
                <span class="text-pretty text-base font-normal md:text-lg">
                  {{ $t(`${prefix}.declaration.timeline.step1.label`) }}
                </span>
              </div>
            </li>
            <li>
              <div class="flex flex-col gap-1">
                <span class="text-sm font-semibold uppercase tracking-wider text-text-accent-700">
                  {{ $t(`${prefix}.declaration.timeline.step2.date`) }}
                </span>
                <span class="text-pretty text-base font-normal md:text-lg">
                  {{ $t(`${prefix}.declaration.timeline.step2.label`) }}
                </span>
              </div>
            </li>
            <li>
              <div class="flex flex-col gap-1">
                <time
                  datetime="2026-11-16"
                  class="text-sm font-semibold uppercase tracking-wider text-text-accent-700"
                >
                  {{ $t(`${prefix}.declaration.timeline.step3.date`) }}
                </time>
                <span class="text-pretty text-base font-normal md:text-lg">
                  {{ $t(`${prefix}.declaration.timeline.step3.label`) }}
                </span>
              </div>
            </li>
          </ol>

          <h3 class="pt-2 text-lg font-medium md:text-xl">{{ $t(`${prefix}.declaration.technologiesTitle`) }}</h3>
          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.declaration.technologies`) }}</p>

          <h3 class="pt-2 text-lg font-medium md:text-xl">{{ $t(`${prefix}.declaration.establishmentTitle`) }}</h3>
          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.declaration.establishment`) }}</p>
        </UiCustomDocumentSection>

        <UiCustomDocumentSection
          anchor="accessibility-scheme"
          :index="2"
          :title="$t(`${prefix}.scheme.title`)"
        >
          <h3 class="text-lg font-medium md:text-xl">{{ $t(`${prefix}.scheme.policyTitle`) }}</h3>
          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.scheme.policyIntro`) }}</p>
          <ul class="flex list-inside list-disc flex-col gap-2 pl-2">
            <li class="text-pretty text-base font-normal md:text-lg">
              {{ $t(`${prefix}.scheme.policyList.compliance`) }}
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">
              <i18n-t
                :keypath="`${prefix}.scheme.policyList.byDesign`"
                scope="global"
              >
                <template #en><span lang="en">accessibility by design</span></template>
              </i18n-t>
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">
              {{ $t(`${prefix}.scheme.policyList.priority`) }}
            </li>
          </ul>

          <h3 class="pt-2 text-lg font-medium md:text-xl">{{ $t(`${prefix}.scheme.governanceTitle`) }}</h3>
          <ul class="flex list-inside list-disc flex-col gap-2 pl-2">
            <li class="text-pretty text-base font-normal md:text-lg">
              <i18n-t
                :keypath="`${prefix}.scheme.governanceList.referent`"
                scope="global"
              >
                <template #email>
                  <NuxtLink
                    to="mailto:rgaa@optionway.com"
                    class="text-text-primary-900 underline underline-offset-2"
                  >
                    rgaa@optionway.com
                  </NuxtLink>
                </template>
              </i18n-t>
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">
              {{ $t(`${prefix}.scheme.governanceList.training`) }}
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">
              {{ $t(`${prefix}.scheme.governanceList.externalSkills`) }}
            </li>
          </ul>

          <h3 class="pt-2 text-lg font-medium md:text-xl">{{ $t(`${prefix}.scheme.scopeTitle`) }}</h3>
          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.scheme.scopeIntro`) }}</p>
          <ul class="flex list-inside list-disc flex-col gap-2 pl-2">
            <li class="text-pretty text-base font-normal md:text-lg">
              {{ $t(`${prefix}.scheme.scopeList.mainSite`) }}
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">
              <i18n-t
                :keypath="`${prefix}.scheme.scopeList.whiteLabel`"
                scope="global"
              >
                <template #en><span lang="en">powered by Option Way</span></template>
              </i18n-t>
            </li>
          </ul>
          <p class="text-pretty text-base font-normal md:text-lg">
            {{ $t(`${prefix}.scheme.scopeSharedResponsibility`) }}
          </p>
          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.scheme.scopeUpdates`) }}</p>

          <h3 class="pt-2 text-lg font-medium md:text-xl">{{ $t(`${prefix}.scheme.projectsTitle`) }}</h3>
          <ul class="flex list-inside list-disc flex-col gap-2 pl-2">
            <li class="text-pretty text-base font-normal md:text-lg">
              {{ $t(`${prefix}.scheme.projectsList.specifications`) }}
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">
              {{ $t(`${prefix}.scheme.projectsList.procurement`) }}
            </li>
          </ul>

          <h3 class="pt-2 text-lg font-medium md:text-xl">{{ $t(`${prefix}.scheme.monitoringTitle`) }}</h3>
          <ul class="flex list-inside list-disc flex-col gap-2 pl-2">
            <li class="text-pretty text-base font-normal md:text-lg">
              {{ $t(`${prefix}.scheme.monitoringList.audit`) }}
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">
              {{ $t(`${prefix}.scheme.monitoringList.tests`) }}
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">
              {{ $t(`${prefix}.scheme.monitoringList.declarationUpdate`) }}
            </li>
          </ul>

          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.scheme.closing`) }}</p>
        </UiCustomDocumentSection>

        <UiCustomDocumentSection
          anchor="accessibility-action-plan"
          :index="3"
          :title="$t(`${prefix}.actionPlan.title`)"
        >
          <div
            tabindex="0"
            role="region"
            :aria-label="$t(`${prefix}.actionPlan.caption`)"
            class="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary-500"
          >
            <table class="w-full border-collapse text-left">
              <caption class="sr-only">
                {{
                  $t(`${prefix}.actionPlan.caption`)
                }}
              </caption>
              <thead>
                <tr class="border-b border-gray-300">
                  <th
                    scope="col"
                    class="py-3 pr-4 text-sm font-semibold uppercase tracking-wider text-text-accent-700"
                  >
                    {{ $t(`${prefix}.actionPlan.headers.action`) }}
                  </th>
                  <th
                    scope="col"
                    class="py-3 pr-4 text-sm font-semibold uppercase tracking-wider text-text-accent-700"
                  >
                    {{ $t(`${prefix}.actionPlan.headers.deadline`) }}
                  </th>
                  <th
                    scope="col"
                    class="w-px py-3 text-sm font-semibold uppercase tracking-wider text-text-accent-700"
                  >
                    {{ $t(`${prefix}.actionPlan.headers.status`) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr class="border-b border-gray-200 align-top">
                  <th
                    scope="row"
                    class="text-pretty py-4 pr-4 text-base font-normal md:text-lg"
                  >
                    {{ $t(`${prefix}.actionPlan.rows.publication.action`) }}
                  </th>
                  <td class="py-4 pr-4 text-base font-normal md:text-lg">
                    <time datetime="2026-08-31">{{ $t(`${prefix}.actionPlan.rows.publication.date`) }}</time>
                  </td>
                  <td class="w-px whitespace-nowrap py-4">
                    <UiCustomBadge
                      variant="green"
                      size="s"
                    >
                      {{ $t(`${prefix}.actionPlan.status.done`) }}
                    </UiCustomBadge>
                  </td>
                </tr>
                <tr class="border-b border-gray-200 align-top">
                  <th
                    scope="row"
                    class="text-pretty py-4 pr-4 text-base font-normal md:text-lg"
                  >
                    {{ $t(`${prefix}.actionPlan.rows.audit.action`) }}
                  </th>
                  <td class="py-4 pr-4 text-base font-normal md:text-lg">
                    <time datetime="2026-11-02">{{ $t(`${prefix}.actionPlan.rows.audit.date`) }}</time>
                  </td>
                  <td class="w-px whitespace-nowrap py-4">
                    <UiCustomBadge
                      variant="orange"
                      size="s"
                    >
                      {{ $t(`${prefix}.actionPlan.status.inProgress`) }}
                    </UiCustomBadge>
                  </td>
                </tr>
                <tr class="border-b border-gray-200 align-top">
                  <th
                    scope="row"
                    class="text-pretty py-4 pr-4 text-base font-normal md:text-lg"
                  >
                    {{ $t(`${prefix}.actionPlan.rows.corrections.action`) }}
                  </th>
                  <td class="py-4 pr-4 text-base font-normal md:text-lg">
                    {{ $t(`${prefix}.actionPlan.rows.corrections.date`) }}
                  </td>
                  <td class="w-px whitespace-nowrap py-4">
                    <UiCustomBadge
                      variant="light-blue"
                      size="s"
                    >
                      {{ $t(`${prefix}.actionPlan.status.upcoming`) }}
                    </UiCustomBadge>
                  </td>
                </tr>
                <tr class="border-b border-gray-200 align-top">
                  <th
                    scope="row"
                    class="text-pretty py-4 pr-4 text-base font-normal md:text-lg"
                  >
                    {{ $t(`${prefix}.actionPlan.rows.fullDeclaration.action`) }}
                  </th>
                  <td class="py-4 pr-4 text-base font-normal md:text-lg">
                    <time datetime="2026-11-16">{{ $t(`${prefix}.actionPlan.rows.fullDeclaration.date`) }}</time>
                  </td>
                  <td class="w-px whitespace-nowrap py-4">
                    <UiCustomBadge
                      variant="light-blue"
                      size="s"
                    >
                      {{ $t(`${prefix}.actionPlan.status.upcoming`) }}
                    </UiCustomBadge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.actionPlan.closing`) }}</p>
        </UiCustomDocumentSection>

        <UiCustomDocumentSection
          anchor="accessibility-report"
          :index="4"
          :title="$t(`${prefix}.report.title`)"
        >
          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.report.intro`) }}</p>

          <p class="text-pretty text-base font-normal md:text-lg">
            <i18n-t
              :keypath="`${prefix}.report.byEmail`"
              scope="global"
            >
              <template #email>
                <NuxtLink
                  to="mailto:rgaa@optionway.com"
                  class="font-semibold text-text-primary-900 underline underline-offset-2"
                >
                  rgaa@optionway.com
                </NuxtLink>
              </template>
            </i18n-t>
          </p>

          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.report.responseDelay`) }}</p>
        </UiCustomDocumentSection>

        <UiCustomDocumentSection
          anchor="accessibility-remedies"
          :index="5"
          :title="$t(`${prefix}.remedies.title`)"
        >
          <p class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.remedies.intro`) }}</p>

          <ul class="flex list-inside list-disc flex-col gap-2 pl-2">
            <li class="text-pretty text-base font-normal md:text-lg">
              <i18n-t
                :keypath="`${prefix}.remedies.list.onlineForm`"
                scope="global"
              >
                <template #link>
                  <NuxtLink
                    to="https://formulaire.defenseurdesdroits.fr"
                    external
                    target="_blank"
                    :title="`formulaire.defenseurdesdroits.fr (${$t('components.common.newWindow')})`"
                    class="text-text-primary-900 underline underline-offset-2"
                  >
                    formulaire.defenseurdesdroits.fr
                  </NuxtLink>
                </template>
              </i18n-t>
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">
              <i18n-t
                :keypath="`${prefix}.remedies.list.delegate`"
                scope="global"
              >
                <template #link>
                  <NuxtLink
                    to="https://www.defenseurdesdroits.fr/carte-des-delegues"
                    external
                    target="_blank"
                    :title="`defenseurdesdroits.fr/carte-des-delegues (${$t('components.common.newWindow')})`"
                    class="text-text-primary-900 underline underline-offset-2"
                  >
                    defenseurdesdroits.fr/carte-des-delegues
                  </NuxtLink>
                </template>
              </i18n-t>
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">
              <i18n-t
                :keypath="`${prefix}.remedies.list.phone`"
                scope="global"
              >
                <template #phone>
                  <NuxtLink
                    to="tel:+33969390000"
                    class="text-text-primary-900 underline underline-offset-2"
                  >
                    09 69 39 00 00
                  </NuxtLink>
                </template>
              </i18n-t>
            </li>
            <li class="text-pretty text-base font-normal md:text-lg">{{ $t(`${prefix}.remedies.list.mail`) }}</li>
          </ul>
        </UiCustomDocumentSection>

        <p class="text-pretty border-t border-gray-200 pt-8 text-base font-normal italic md:text-lg">
          {{ $t(`${prefix}.publishedAt`) }}
        </p>
      </div>
    </div>
  </div>
</template>
